import { useEffect, useState } from "react"
import { Link } from "react-router-dom"
import { SEO } from "@/components/SEO"
import { Interface, formatUnits } from "ethers"
import { Button } from "@/components/ui/button"
import { Card, CardContent } from "@/components/ui/card"
import { ArrowRight, Coins, TrendingUp, Globe, BookOpen, Copy, Check } from "lucide-react"
import { formatCompactWelephant, formatPrice } from "@/lib/ethersUtils"
import { SWAPPER_ADDRESS, WELEPHANT_ADDRESS, GAME_CHAIN_ID, getMulticall3AddressForChain } from "@/lib/contracts"
import { getChainProvider } from "@/lib/chainProvider"
import { WelephantSwapABI } from "@/abi/WelephantSwapABI"
import { createMulticallService } from "@/services/multicallService"
import { isPageVisible } from "@/lib/pageVisibility"
import { usePoolLiquidity } from "@/hooks/usePoolLiquidity"
import { Coin3D } from "@/components/Coin3D"
import welephantLogo from "@/assets/tokens/welephant.svg"

// ERC20 ABI for total supply
const ERC20_ABI = [
  "function totalSupply() view returns (uint256)",
  "function decimals() view returns (uint8)",
] as const

function formatUsdCompact(value: number): string {
  if (value >= 1000000) return `$${(value / 1000000).toFixed(2)}M`
  if (value >= 1000) return `$${(value / 1000).toFixed(1)}K`
  return `$${value.toFixed(0)}`
}

export function VanillaHomePage() {
  const [welephantPrice, setWelephantPrice] = useState<number | null>(null)
  const [marketCap, setMarketCap] = useState<number | null>(null)
  const [totalSupply, setTotalSupply] = useState<bigint | null>(null)
  const [isLoading, setIsLoading] = useState(true)

  // All LP pools backing WELEPHANT (WELEPHANT + ELEPHANT pairs, both chains)
  const { pools, loading: poolsLoading } = usePoolLiquidity()
  const liquidityBacking = pools.reduce((sum, p) => sum + (p.totalValueUsd ?? 0), 0)

  const [copiedAddress, setCopiedAddress] = useState(false)
  const copyAddress = async () => {
    await navigator.clipboard.writeText(WELEPHANT_ADDRESS)
    setCopiedAddress(true)
    setTimeout(() => setCopiedAddress(false), 2000)
  }

  useEffect(() => {
    const fetchStats = async () => {
      try {
        if (!isPageVisible()) return
        const provider = await getChainProvider(GAME_CHAIN_ID)

        const swapInterface = new Interface(WelephantSwapABI)
        const erc20Interface = new Interface(ERC20_ABI)

        const multicall = createMulticallService(provider, getMulticall3AddressForChain(GAME_CHAIN_ID), GAME_CHAIN_ID)

        const calls = [
          multicall.encodeCall(SWAPPER_ADDRESS, swapInterface, 'getWelephantPriceInUSD'),
          multicall.encodeCall(WELEPHANT_ADDRESS, erc20Interface, 'totalSupply'),
          multicall.encodeCall(WELEPHANT_ADDRESS, erc20Interface, 'decimals'),
        ]

        const [priceInUsd, supply, decimals] =
          await multicall.callAndDecode<[bigint | null, bigint | null, bigint | null]>(calls)

        if (priceInUsd !== null && supply !== null && decimals !== null) {
          const price = parseFloat(formatUnits(priceInUsd, 18))
          setWelephantPrice(price)
          setTotalSupply(supply)

          const totalSupplyNum = parseFloat(formatUnits(supply, Number(decimals)))
          setMarketCap(price * totalSupplyNum)
        }
      } catch (error) {
        console.error('Failed to fetch token stats:', error)
      } finally {
        setIsLoading(false)
      }
    }

    fetchStats()
    const interval = setInterval(fetchStats, 10000)
    return () => clearInterval(interval)
  }, [])

  return (
    <>
      <SEO
        url="/"
        description="WELEPHANT — a community token on BNB Chain with liquid staking and cross-chain bridging. Swap, stake, and hold. Self-custody always."
        keywords="WELEPHANT, BNB Chain, token, DeFi, liquid staking, stWELEPHANT, Wormhole bridge, self-custody"
      />
      <div className="flex flex-col">
        {/* Hero Section */}
        <section className="relative overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-br from-primary/5 via-transparent to-primary/10" />
          <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,_var(--tw-gradient-stops))] from-primary/10 via-transparent to-transparent" />

          <div className="container relative py-24 md:py-32">
            <div className="grid md:grid-cols-2 gap-12 items-center max-w-5xl mx-auto">
              <div className="flex flex-col gap-6 text-center md:text-left">
                <h1 className="text-4xl font-bold tracking-tighter sm:text-5xl md:text-6xl lg:text-7xl bg-gradient-to-r from-foreground to-foreground/70 bg-clip-text">
                  WELEPHANT
                </h1>
                <p className="text-2xl font-semibold text-foreground/90">
                  Deep liquidity. Real yield. Zero admin keys.
                </p>
                <p className="text-lg text-muted-foreground max-w-lg">
                  WELEPHANT lives on BNB Chain. Swap in, stake for yield with
                  stWELEPHANT, and bridge across chains. No custodian, no
                  gatekeepers — your money stays yours.
                </p>

                <div className="flex flex-wrap gap-4 mt-4 justify-center md:justify-start">
                  <Button asChild size="lg" className="gap-2">
                    <Link to="/swap">
                      Swap
                      <ArrowRight className="h-4 w-4" />
                    </Link>
                  </Button>
                  <Button variant="outline" size="lg" asChild>
                    <Link to="/stake">Stake</Link>
                  </Button>
                  <Button variant="outline" size="lg" asChild>
                    <Link to="/bridge">Bridge</Link>
                  </Button>
                </div>
              </div>

              <div className="flex items-center justify-center">
                <Coin3D className="h-48 w-48 md:h-64 md:w-64" />
              </div>
            </div>
          </div>
        </section>

        {/* Live Token Stats */}
        <section className="bg-muted/50">
          <div className="container py-16 md:py-24">
            <div className="text-center mb-12">
              <h2 className="text-3xl font-bold tracking-tighter">
                Live From the Blockchain
              </h2>
              <p className="mt-2 text-muted-foreground">
                Real-time data. Not our numbers — the contract's.
              </p>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-8 text-center max-w-4xl mx-auto">
              <div>
                <div className="text-4xl font-bold">
                  {isLoading ? (
                    <span className="text-muted-foreground">...</span>
                  ) : welephantPrice !== null ? (
                    formatPrice(welephantPrice)
                  ) : (
                    "—"
                  )}
                </div>
                <div className="text-sm text-muted-foreground">WELEPHANT Price</div>
              </div>
              <div>
                <div className="text-4xl font-bold">
                  {isLoading ? (
                    <span className="text-muted-foreground">...</span>
                  ) : marketCap !== null ? (
                    formatUsdCompact(marketCap)
                  ) : (
                    "—"
                  )}
                </div>
                <div className="text-sm text-muted-foreground">Market Cap</div>
              </div>
              <div>
                <div className="text-4xl font-bold">
                  {isLoading ? (
                    <span className="text-muted-foreground">...</span>
                  ) : totalSupply !== null ? (
                    formatCompactWelephant(totalSupply)
                  ) : (
                    "—"
                  )}
                </div>
                <div className="text-sm text-muted-foreground">Total Supply</div>
              </div>
              <div>
                <div className="text-4xl font-bold">
                  {poolsLoading && pools.length === 0 ? (
                    <span className="text-muted-foreground">...</span>
                  ) : pools.length > 0 ? (
                    formatUsdCompact(liquidityBacking)
                  ) : (
                    "—"
                  )}
                </div>
                <div className="text-sm text-muted-foreground">Liquidity</div>
              </div>
            </div>

            {/* Contract address — second row of the stats block */}
            {WELEPHANT_ADDRESS && (
              <div className="mt-10 max-w-4xl mx-auto text-center">
                <button
                  onClick={copyAddress}
                  title="Copy contract address"
                  className="group inline-flex max-w-full items-center justify-center gap-3 font-mono text-lg md:text-xl font-bold hover:text-foreground/80 transition-colors"
                >
                  <img src={welephantLogo} alt="" className="h-6 w-6 shrink-0" />
                  <span className="hidden md:inline">{WELEPHANT_ADDRESS}</span>
                  <span className="md:hidden">
                    {`${WELEPHANT_ADDRESS.slice(0, 8)}...${WELEPHANT_ADDRESS.slice(-6)}`}
                  </span>
                  {copiedAddress ? (
                    <Check className="h-5 w-5 shrink-0 text-green-500" />
                  ) : (
                    <Copy className="h-5 w-5 shrink-0 opacity-60 group-hover:opacity-100" />
                  )}
                </button>
                <div className="text-sm text-muted-foreground mt-1">Contract Address</div>
              </div>
            )}
          </div>
        </section>

        {/* About the Token */}
        <section className="container py-16 md:py-24">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold tracking-tighter">
              One Token, Built to Last
            </h2>
            <p className="mt-2 text-muted-foreground">
              Simple mechanics, transparent economics, verifiable on-chain.
            </p>
          </div>
          <div className="grid md:grid-cols-3 gap-6 max-w-4xl mx-auto">
            <Card>
              <CardContent className="pt-6 flex flex-col gap-3">
                <Coins className="h-8 w-8 text-primary" />
                <h3 className="text-xl font-semibold">Tokenomics</h3>
                <p className="text-sm text-muted-foreground flex-1">
                  Fixed rules, open distribution, and an economic model anyone
                  can audit on-chain.
                </p>
                <Link to="/docs/tokenomics" className="text-sm font-medium text-primary inline-flex items-center gap-1">
                  Read the tokenomics
                  <ArrowRight className="h-3.5 w-3.5" />
                </Link>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-6 flex flex-col gap-3">
                <TrendingUp className="h-8 w-8 text-primary" />
                <h3 className="text-xl font-semibold">Liquid Staking</h3>
                <p className="text-sm text-muted-foreground flex-1">
                  Stake WELEPHANT for stWELEPHANT, an ERC-4626 vault token that
                  compounds while staying liquid.
                </p>
                <Link to="/stake" className="text-sm font-medium text-primary inline-flex items-center gap-1">
                  Start staking
                  <ArrowRight className="h-3.5 w-3.5" />
                </Link>
                <Link to="/docs/staking" className="text-sm text-muted-foreground inline-flex items-center gap-1">
                  How staking works
                </Link>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-6 flex flex-col gap-3">
                <Globe className="h-8 w-8 text-primary" />
                <h3 className="text-xl font-semibold">Cross-Chain</h3>
                <p className="text-sm text-muted-foreground flex-1">
                  Bridge WELEPHANT across chains with Wormhole. Your token,
                  wherever you need it.
                </p>
                <Link to="/bridge" className="text-sm font-medium text-primary inline-flex items-center gap-1">
                  Bridge tokens
                  <ArrowRight className="h-3.5 w-3.5" />
                </Link>
                <Link to="/docs/bridging" className="text-sm text-muted-foreground inline-flex items-center gap-1">
                  How bridging works
                </Link>
              </CardContent>
            </Card>
          </div>
          <div className="text-center mt-12">
            <Button variant="outline" size="lg" asChild className="gap-2">
              <Link to="/docs/manifesto">
                <BookOpen className="h-4 w-4" />
                Read the Manifesto
              </Link>
            </Button>
          </div>
        </section>
      </div>
    </>
  )
}
